import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { MarketType } from '@/lib/hyperliquid';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface AttributionBreakdown {
  totalPnl: number;
  marketBeta: number;
  leverageContribution: number;
  timingAlpha: number;
  sizingAlpha: number;
  feesDrag: number;
  fundingDrag: number;
}

export interface CoinAttribution {
  coin: string;
  totalPnl: number;
  marketBeta: number;
  timingAlpha: number;
  sizingAlpha: number;
  fees: number;
  funding: number;
  tradeCount: number;
  totalVolume: number;
  avgPositionSize: number;
  winRate: number;
}

export interface StrategySegment {
  strategyType: 'scalp' | 'swing' | 'positional';
  tradeCount: number;
  totalPnl: number;
  totalVolume: number;
  winRate: number | null;
  avgHoldTimeMinutes: number;
  sharpe: number | null;
  avgLeverage: number | null;
}

export interface PositionSizeBucket {
  sizeBucket: string;
  bucketMin: number;
  bucketMax: number;
  tradeCount: number;
  totalPnl: number;
  avgPnlPerTrade: number;
  winRate: number | null;
  maxLoss: number;
  maxGain: number;
}

export interface AttributionData {
  attribution: AttributionBreakdown;
  coinBreakdown: CoinAttribution[];
  strategySegments: StrategySegment[];
  positionSizing: PositionSizeBucket[];
  avgLeverage: number | null;
  totalVolume: number;
  tradeCount: number;
  winRate: number | null;
  insights: string[];
}

interface UseAttributionOptions {
  address: string | undefined;
  rangeDays?: number;
  marketType?: MarketType;
}

interface UseAttributionReturn {
  data: AttributionData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAttribution({
  address,
  rangeDays = 30,
  marketType = 'perps',
}: UseAttributionOptions): UseAttributionReturn {
  const { toast } = useToast();
  const [data, setData] = useState<AttributionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAttribution = useCallback(async () => {
    if (!address) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/hl-attribution?address=${encodeURIComponent(address)}&rangeDays=${rangeDays}&marketType=${encodeURIComponent(marketType)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch attribution data');
      }

      const attributionData = await response.json();
      setData(attributionData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch attribution data';
      setError(message);
      console.error('Attribution fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [address, rangeDays, marketType]);

  useEffect(() => {
    if (address) {
      fetchAttribution();
    }
  }, [address, rangeDays, marketType, fetchAttribution]);

  return {
    data,
    isLoading,
    error,
    refresh: fetchAttribution,
  };
}
